/**
 * Design constants for Review's two surfaces.
 *
 * Review is the one product in the suite that wears both themes at once, and
 * that is a design decision rather than an oversight. The design handoff is
 * explicit about it:
 *
 *   > Two shells, deliberately different: the developer side is the frameless
 *   > Qt-style Mauvely window; the reviewer side is a plain light web page.
 *   > Do not unify them.
 *
 * The author is in a tool, alongside Compose and Snap, and gets the dark app
 * ground. The reviewer is a guest who has been sent a link — often a client, who
 * may never have heard of Mauvely — and gets a light page that reads as a
 * document rather than as software. So `data-theme` is set per surface here,
 * not by a user preference, and there is no theme toggle.
 *
 * Everything below is `var(--*)` from the design system wherever the token set
 * has an answer. The literals are the handful of values the mockups hard-coded
 * outside it.
 */

import type React from 'react';

export const EASE = 'cubic-bezier(.22,.61,.36,1)';

/**
 * App chrome, decided 2026-09-09 — the readme's § "App chrome" section.
 *
 * The window ground is violet-tinted; a rail or sidebar is a panel of its own on
 * it, one step lighter and still tinted; every content region — the course
 * canvas, the comment inspector — is a *neutral* panel at 16px radius, with 16px
 * of ground showing between and around. Cards on a panel stay `--surface-card`.
 *
 * **Render from the `var()` names, not from these numbers.** The literals are
 * here so the decision is readable in TypeScript and so a value can be checked
 * without opening the token file — they mirror the `--app-*` block at the end of
 * each theme in `web/public/_ds/mauvely/tokens/colors.css`, which is the only
 * definition the browser sees. Nothing here switches on `data-theme`, so a
 * hardcoded hex will not follow the surface.
 *
 * This is the app origin only. `preview.mauvely.com` serves Compose's vendored
 * runtime (`web/public/rt/`) and is not ours to restyle.
 */
export const APP = {
  light: {
    ground: '#e8e5f9',
    chromePanel: '#f5f3ff',
    /** slate-50 */
    panel: '#f8f9fd',
    /** slate-100 — strips inside a panel: a toolbar, a tab strip. */
    panelAlt: '#f3f5fe',
    /** Inputs, still reading as inset now the panel under them is lighter. */
    panelSunken: '#eef0f8'
  },
  dark: {
    /** indigo-950 */
    ground: '#12142e',
    chromePanel: '#1b1a33',
    panel: '#1b1d2b',
    panelAlt: '#1f2131',
    panelSunken: '#161826'
  }
} as const;

/** Chrome metrics, from the mockup's inline styles. */
export const LAYOUT = {
  /** The app bar. 44px in the mockup, where it stood in for a title bar. */
  topbar: 44,
  /** Outline / Comments rail on the left of the author workspace. */
  sidebar: 300,
  /** The comment rail. Lives *inside* the course document — see
   *  runtime/review-overlay.js — so this is the gutter it reserves there. */
  rail: 340,
  statusStrip: 30,
  /** The ground showing between panels and around them — `--gap-grid`, and the
   *  same 16 the native apps use. */
  gutter: 16
} as const;

export const RADIUS = {
  panel: 22,
  card: 16,
  control: 10,
  chip: 8,
  pill: 999,
  /** `--radius-xl`. A chrome panel on the app ground. */
  appPanel: 16
} as const;

/**
 * Motion, from the readme's § Motion. Milliseconds, matching `motion.css`.
 *
 * Anything expressible in CSS is: `--transition-control`, `.mv-page` and
 * `.mv-pop` in `styles.css` carry the shapes and `prefers-reduced-motion` is
 * handled there in one place. These are for the few transitions that have to be
 * inline because the value is computed in React.
 */
export const DUR = {
  /** Hover and press colour. */
  fast: 120,
  /** Reveals, tab switches, the tab marker sliding. */
  base: 180,
  /** Dialogs and drawers. */
  slow: 280
} as const;

/**
 * Reviewer identity colours.
 *
 * Violet and mauve ramps only. Teal is a locked signal colour in this brand —
 * it means live, complete or resolved — so it is never handed to a person, and
 * violet 400 is the author's chip, never dealt to a reviewer. The server
 * assigns colours (worker/lib/colours.ts); this table exists so the client can
 * pair whichever background arrives with its legible ink.
 */
export const REVIEWER_COLOURS = [
  { bg: '#886193', ink: '#f3f5fe' },
  { bg: '#796cbf', ink: '#f3f5fe' },
  { bg: '#ac89b6', ink: '#281c2d' },
  // The author's.
  { bg: '#b5abfc', ink: '#1a1728' }
] as const;

/** Baloo 2 800. Titles in the Mauvely voice end in a full stop. */
export const display = (size: number): React.CSSProperties => ({
  fontFamily: "var(--font-display), 'Baloo 2', system-ui",
  fontWeight: 800,
  fontSize: size,
  lineHeight: size >= 32 ? 1.04 : 1.1,
  letterSpacing: '-.015em',
  color: 'var(--text-strong)',
  margin: 0
});

/** Eyebrow labels: mono, wide tracking, uppercase. */
export const mono: React.CSSProperties = {
  fontFamily: "var(--font-mono), 'JetBrains Mono', monospace",
  fontSize: 10.5,
  letterSpacing: '.17em',
  textTransform: 'uppercase',
  color: 'var(--text-faint)'
};

/** Metadata lines: mono, joined with `·`, never commas and never emoji. */
export const monoPlain: React.CSSProperties = {
  fontFamily: "var(--font-mono), 'JetBrains Mono', monospace",
  fontSize: 11,
  color: 'var(--text-muted)'
};

// ── formatting ──────────────────────────────────────────────────────────────

/**
 * Relative for the last week, then an absolute short date.
 *
 * Copied from website-cloud's theme.ts rather than imported, because the three
 * web apps share no package. If you change one, change the others.
 */
export function formatWhen(unixSeconds: number): string {
  const then = new Date(unixSeconds * 1000);
  const diff = Date.now() / 1000 - unixSeconds;

  if (diff < 60) return 'just now';
  if (diff < 3600) {
    const m = Math.floor(diff / 60);
    return `${m} minute${m === 1 ? '' : 's'} ago`;
  }
  if (diff < 86400) {
    const h = Math.floor(diff / 3600);
    return `${h} hour${h === 1 ? '' : 's'} ago`;
  }
  if (diff < 172800) return 'yesterday';
  if (diff < 604800) return then.toLocaleDateString('en-GB', { weekday: 'long' });
  return then.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

/**
 * Short relative, for a comment card: "3d", "4h", "now".
 *
 * The mockup's comment cards use this compact form rather than `formatWhen`,
 * because a rail card has no room for "3 hours ago" beside a name and a tag.
 */
export function formatAgo(unixSeconds: number): string {
  const diff = Math.max(0, Date.now() / 1000 - unixSeconds);
  if (diff < 60) return 'now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

/** "AR" for "Ada Rowe", falling back to the address. */
export function initials(name: string, email: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
  if (parts.length === 1 && parts[0]!.length >= 2) return parts[0]!.slice(0, 2).toUpperCase();
  return email.slice(0, 2).toUpperCase();
}
